// Smart guides while a wall item is dragged: collects the other wall items once
// per drag, snaps the dragged one onto a nearby edge/centre (domain/wallGuides)
// and draws the neon guide lines. Floor furniture never comes here — it stays
// completely free.
import type * as PIXI from 'pixi.js';
import { isWallMountedItem } from '../../../domain/tileMath';
import { readFurnitureData } from '../../../domain/sceneNode';
import type { WindowSnapResult } from '../../../domain/windowSnap';
import {
  findWallGuides,
  matchingGuides,
  wallBoxFromBounds,
  wallGuideSegment,
  wallGuidesKey,
  WALL_GUIDE_SNAP_SCREEN_PX,
  type WallBox,
  type WallGuide,
  type WallSide,
} from '../../../domain/wallGuides';
import { selectionChanged } from '../../../utils/telegram/hapticFeedback';

const GUIDE_COLOR = 0x39ff14; // neon green
const GUIDE_WIDTH_SCREEN_PX = 1.5;
const GLOW_WIDTH_SCREEN_PX = 5;

export interface WallGuideDragState {
  /** Other wall items, measured once at the first move of the drag. */
  wallGuideBoxes?: WallBox[];
  /** Key of the guides shown last frame — a change is a snap (haptic tick). */
  wallGuideKey?: string;
}

/**
 * Whether the drag pushes a wall item past the FAR end of its wall (its position
 * along the wall is clamped there). On the same wall the snap follows x exactly,
 * so any difference between the dragged x and the snapped x is the overshoot.
 */
export function wallFarEndOvershoot(ws: WindowSnapResult, draggedX: number, previous?: WindowSnapResult): boolean {
  // A wall change re-places the item by tiles — not an overshoot.
  if (!ws.isOnWall || !previous || previous.shouldMirror !== ws.shouldMirror) return false;
  return ws.shouldMirror ? draggedX < ws.x : draggedX > ws.x;
}

/** Room-local box of a sprite. */
function roomBox(sprite: PIXI.Container, room: PIXI.Container) {
  const b = sprite.getBounds();
  const a = room.toLocal({ x: b.x, y: b.y });
  const z = room.toLocal({ x: b.x + b.width, y: b.y + b.height });
  return { x0: a.x, y0: a.y, x1: z.x, y1: z.y };
}

function collectWallBoxes(markers: Map<number, PIXI.Container>, excludeId: number, room: PIXI.Container): WallBox[] {
  const boxes: WallBox[] = [];
  for (const [id, marker] of markers) {
    if (id === excludeId || !marker.visible) continue;
    const sprite = marker.children[0] as PIXI.Sprite | undefined;
    const fData = readFurnitureData(marker, sprite);
    if (!sprite || !fData || !isWallMountedItem(fData.furniture_catalog)) continue;
    // Mirrored wall items hang on the left wall (see snapWindowAnchorToGrid).
    const side: WallSide = sprite.scale.x < 0 ? 'left' : 'right';
    boxes.push(wallBoxFromBounds(side, roomBox(sprite, room)));
  }
  return boxes;
}

export interface WallGuideSnapInput {
  state: WallGuideDragState;
  room: PIXI.Container;
  markers: Map<number, PIXI.Container>;
  furnitureId: number;
  marker: PIXI.Container;
  /** Already facing the wall of `ws` (mirror applied). */
  sprite: PIXI.Container;
  ws: WindowSnapResult;
  wallOffset: number;
  /** Re-run the wall snap with the anchor moved by dx along x. */
  resnapX: (dx: number) => WindowSnapResult;
  clampOffset: (offset: number) => number;
}

/** Snap the dragged wall item onto nearby guides; returns where it goes and what to draw. */
export function snapWallItemToGuides(input: WallGuideSnapInput): {
  ws: WindowSnapResult;
  wallOffset: number;
  guides: WallGuide[];
} {
  const { state, room, sprite, marker } = input;
  let { ws, wallOffset } = input;
  if (!ws.isOnWall) return { ws, wallOffset, guides: [] };
  state.wallGuideBoxes ??= collectWallBoxes(input.markers, input.furnitureId, room);
  const others = state.wallGuideBoxes;
  if (!others.length) return { ws, wallOffset, guides: [] };

  const side: WallSide = ws.shouldMirror ? 'left' : 'right';
  // The sprite's box relative to its marker, moved to where this frame puts it.
  const now = roomBox(sprite, room);
  const boxAt = (x: number, y: number) => wallBoxFromBounds(side, {
    x0: now.x0 - marker.position.x + x, x1: now.x1 - marker.position.x + x,
    y0: now.y0 - marker.position.y + y, y1: now.y1 - marker.position.y + y,
  });

  const zoom = Math.abs(room.worldTransform.a) || 1;
  const snap = findWallGuides(boxAt(ws.x, ws.y + wallOffset), others, WALL_GUIDE_SNAP_SCREEN_PX / zoom);
  if (snap.du) {
    const moved = input.resnapX(side === 'left' ? -snap.du : snap.du);
    if (moved.shouldMirror === ws.shouldMirror && moved.isOnWall) ws = moved;
  }
  if (snap.dv) wallOffset = input.clampOffset(wallOffset - snap.dv); // up = smaller offset
  // A wall end or the wall top may have stopped the shift — show only real matches.
  const guides = snap.du || snap.dv ? matchingGuides(boxAt(ws.x, ws.y + wallOffset), others) : snap.guides;
  return { ws, wallOffset, guides };
}

/** Draw the guides (or clear them) and tick the haptic when a new alignment is hit. */
export function drawWallGuides(
  overlay: PIXI.Graphics | null,
  state: WallGuideDragState,
  guides: WallGuide[],
  room: PIXI.Container,
): void {
  const key = wallGuidesKey(guides);
  if (key && key !== state.wallGuideKey) selectionChanged();
  state.wallGuideKey = key;
  if (!overlay) return;
  overlay.clear();
  overlay.visible = guides.length > 0;
  if (!guides.length) return;
  const px = 1 / (Math.abs(room.worldTransform.a) || 1);
  const segments = guides.flatMap((g) => (g.axis === 'v' ? g.sides : [g.side]).map((s) => wallGuideSegment(g, s)));
  for (const [width, alpha] of [[GLOW_WIDTH_SCREEN_PX, 0.25], [GUIDE_WIDTH_SCREEN_PX, 1]] as const) {
    for (const s of segments) overlay.moveTo(s.x0, s.y0).lineTo(s.x1, s.y1);
    overlay.stroke({ color: GUIDE_COLOR, width: width * px, alpha });
  }
}

export function clearWallGuides(overlay: PIXI.Graphics | null): void {
  if (!overlay) return;
  overlay.clear();
  overlay.visible = false;
}
